import { createTheme, Theme } from '@mui/material/styles';

declare module '@mui/private-theming/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface DefaultTheme extends Theme {}
}

declare module '@mui/material/Chip' {
  export interface ChipPropsColorOverrides {
    orange: true;
    grayBlue: true;
    blueLight: true;
    blue: true;
    purple: true;
    indigo: true;
    rose: true;
  }
}

declare module '@mui/material/SvgIcon' {
  export interface SvgIconPropsColorOverrides {
    orange: true;
    grayBlue: true;
    blueLight: true;
    blue: true;
    purple: true;
    indigo: true;
    rose: true;
  }
}

declare module '@mui/material/styles' {
  interface Theme {
    shadowSizes: {
      xs: string;
      sm: string;
      md: string;
      lg: string;
      xl: string;
      '2xl': string;
      '3xl': string;
    };
  }

  interface ThemeOptions {
    shadowSizes?: {
      xs: string;
      sm: string;
      md: string;
      lg: string;
      xl: string;
      '2xl': string;
      '3xl': string;
    };
  }

  interface Palette {
    brand: {
      grey: string;
      green1: string;
      green2: string;
      grey1: string;
      grey2: string;
    };
    grayBlue?: PaletteColor;
    blueLight?: PaletteColor;
    blue?: PaletteColor;
    orange?: PaletteColor;
    purple?: PaletteColor;
    indigo?: PaletteColor;
    rose?: PaletteColor;
  }

  interface PaletteOptions {
    brand?: {
      grey?: string;
      green1?: string;
      green2?: string;
      grey1?: string;
      grey2?: string;
    };
    grayBlue?: PaletteColor;
    blueLight?: PaletteColor;
    blue?: PaletteColor;
    orange?: PaletteColor;
    purple?: PaletteColor;
    indigo?: PaletteColor;
    rose?: PaletteColor;
  }

  interface TypographyVariants {
    display2Xl: React.CSSProperties;
    displayXl: React.CSSProperties;
    displayLg: React.CSSProperties;
    displayMd: React.CSSProperties;
    displaySm: React.CSSProperties;
    displayXs: React.CSSProperties;
    textXl: React.CSSProperties;
    textLg: React.CSSProperties;
    textMd: React.CSSProperties;
    textSm: React.CSSProperties;
    textXs: React.CSSProperties;
  }

  interface SimplePaletteColorOptions {
    25?: string;
    50?: string;
    100?: string;
    200?: string;
    300?: string;
    400?: string;
    600?: string;
    700?: string;
    800?: string;
    900?: string;
    950?: string;
    main: string;
  }

  interface PaletteColor {
    light: string;
    main: string;
    dark: string;
    contrastText: string;
    25?: string;
    50?: string;
    100?: string;
    200?: string;
    300?: string;
    400?: string;
    600?: string;
    700?: string;
    800?: string;
    900?: string;
    950?: string;
  }

  interface TypographyVariantsOptions {
    display2Xl?: React.CSSProperties;
    displayXl?: React.CSSProperties;
    displayLg?: React.CSSProperties;
    displayMd?: React.CSSProperties;
    displaySm?: React.CSSProperties;
    displayXs?: React.CSSProperties;
    textXl?: React.CSSProperties;
    textLg?: React.CSSProperties;
    textMd?: React.CSSProperties;
    textSm?: React.CSSProperties;
    textXs?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    display2Xl: true;
    displayXl: true;
    displayLg: true;
    displayMd: true;
    displaySm: true;
    displayXs: true;
    textXl: true;
    textLg: true;
    textMd: true;
    textSm: true;
    textXs: true;
    h6: false;
    h5: false;
    h4: false;
    h3: false;
    h2: false;
    h1: false;
    subtitle1: false;
    subtitle2: false;
    body1: false;
    body2: false;
    button: false;
    caption: false;
  }
}

const shadowSizes = {
  xs: '0px 1px 2px 0px rgba(16, 24, 40, 0.1)',
  sm: '0px 1px 2px 0px rgba(16, 24, 40, 0.1), 0px 1px 3px 0px rgba(16, 24, 40, 0.2)',
  md: '0px 2px 4px -2px rgba(16, 24, 40, 0.1), 0px 4px 8px -2px rgba(16, 24, 40, 0.2)',
  lg: '0px 4px 6px -2px rgba(16, 24, 40, 0.1), 0px 12px 16px -4px rgba(16, 24, 40, 0.2)',
  xl: '0px 8px 8px -4px rgba(16, 24, 40, 0.1), 0px 20px 24px -4px rgba(16, 24, 40, 0.2)',
  '2xl': '0px 24px 48px -12px rgba(16, 24, 40, 0.2)',
  '3xl': '0px 32px 64px -12px rgba(16, 24, 40, 0.2)',
};

const theme = createTheme({
  spacing: (factor: number) => `${0.25 * factor}rem`,
  shadowSizes,
  shadows: [
    'none',
    shadowSizes.xs,
    shadowSizes.sm,
    shadowSizes.md,
    shadowSizes.lg,
    shadowSizes.xl,
    shadowSizes['2xl'],
    shadowSizes['3xl'],
    shadowSizes['3xl'],
    shadowSizes['3xl'],
    shadowSizes['3xl'],
    shadowSizes['3xl'],
    shadowSizes['3xl'],
    shadowSizes['3xl'],
    shadowSizes['3xl'],
    shadowSizes['3xl'],
    shadowSizes['3xl'],
    shadowSizes['3xl'],
    shadowSizes['3xl'],
    shadowSizes['3xl'],
    shadowSizes['3xl'],
    shadowSizes['3xl'],
    shadowSizes['3xl'],
    shadowSizes['3xl'],
    shadowSizes['3xl'],
  ],
  palette: {
    background: {
      default: '#F8FBFE',
    },
    grayBlue: {
      25: '#FCFCFD',
      50: '#F8F9FC',
      100: '#EAECF5',
      200: '#D5D9EB',
      300: '#B3B8DB',
      400: '#717BBC',
      main: '#4E5BA6',
      600: '#3E4784',
      700: '#363F72',
      800: '#293056',
      900: '#101323',
      950: '#0D0F1C',
      contrastText: '#000000',
      light: '#000000',
      dark: '#000000',
    },
    blueLight: {
      25: '#f5fbff',
      50: '#f0f9ff',
      100: '#e0f2fe',
      200: '#b9e6fe',
      300: '#7cd4fd',
      400: '#36bffa',
      main: '#0ba5ec',
      600: '#0086c9',
      700: '#026aa2',
      800: '#065986',
      900: '#0b4a6f',
      950: '#062c41',
      contrastText: '#000000',
      light: '#000000',
      dark: '#000000',
    },
    blue: {
      25: '#f5fbff',
      50: '#eff8ff',
      100: '#d1e9ff',
      200: '#b2ddff',
      300: '#84caff',
      400: '#53b1fd',
      main: '#2e90fa',
      600: '#1570ef',
      700: '#175cd3',
      800: '#1849a9',
      900: '#194185',
      950: '#102a56',
      contrastText: '#000000',
      light: '#000000',
      dark: '#000000',
    },
    brand: {
      grey: '#F4F4F4',
      green1: '#84CC16',
      green2: '#65A30D',
      grey1: '#334155',
      grey2: '#1E293B',
    },
    primary: {
      25: '#FAFBFC',
      50: '#F3F6FA',
      100: '#DCE0E7',
      200: '#B7C2D2',
      300: '#8894A6',
      400: '#4B586B',
      main: '#334155',
      600: '#26364D',
      700: '#0F223F',
      800: '#091B36',
      900: '#04142B',
      950: '#010A17',
      contrastText: '#000000',
      light: '#000000',
      dark: '#000000',
    },
    secondary: {
      25: '#FBFDFF',
      50: '#F8FBFE',
      100: '#F6FBFF',
      200: '#E5ECF2',
      300: '#D5E1EB',
      400: '#A8BCCB',
      main: '#89A1B2',
      600: '#798E9C',
      700: '#66757F',
      800: '#4D5A62',
      900: '#30373C',
      950: '#191D1F',
      contrastText: '#000000',
      light: '#000000',
      dark: '#000000',
    },
    error: {
      25: '#FFFBFA',
      50: '#FEF3F2',
      100: '#FEE4E2',
      200: '#FECDCA',
      300: '#FDA29B',
      400: '#F97066',
      main: '#F04438',
      600: '#D92D20',
      700: '#B42318',
      800: '#912018',
      900: '#7A271A',
      950: '#55160C',
      contrastText: '#000000',
      light: '#000000',
      dark: '#000000',
    },
    warning: {
      25: '#FFFCF5',
      50: '#FFFAEB',
      100: '#FEF0C7',
      200: '#FEDF89',
      300: '#FEC84B',
      400: '#FDB022',
      main: '#F79009',
      600: '#DC6803',
      700: '#B54708',
      800: '#93370D',
      900: '#7A2E0E',
      950: '#4E1D09',
      contrastText: '#000000',
      light: '#000000',
      dark: '#000000',
    },
    success: {
      25: '#FBFFF5',
      50: '#F8FFEE',
      100: '#F0FFD9',
      200: '#D5EFAD',
      300: '#BBE57A',
      400: '#84CC16',
      main: '#7EBC25',
      600: '#65A30D',
      700: '#4A7D03',
      800: '#365608',
      900: '#2C4606',
      950: '#1E3103',
      contrastText: '#000000',
      light: '#000000',
      dark: '#000000',
    },
    orange: {
      25: '#FEFAF5',
      50: '#FEF6EE',
      100: '#FDEAD7',
      200: '#F9DBAF',
      300: '#F7B27A',
      400: '#F38744',
      main: '#EF6820',
      600: '#E04F16',
      700: '#B93815',
      800: '#932F19',
      900: '#772917',
      950: '#511C10',
      contrastText: '#000000',
      light: '#000000',
      dark: '#000000',
    },
    purple: {
      25: '#FAFAFF',
      50: '#F4F3FF',
      100: '#EBE9FE',
      200: '#D9D6FE',
      300: '#BDB4FE',
      400: '#9B8AFB',
      main: '#7A5AF8',
      600: '#6938EF',
      700: '#5925DC',
      800: '#4A1FB8',
      900: '#3E1C96',
      950: '#27115F',
      contrastText: '#000000',
      light: '#000000',
      dark: '#000000',
    },
    indigo: {
      25: '#F5F8FF',
      50: '#EEF4FF',
      100: '#E0EAFF',
      200: '#C7D7FE',
      300: '#A4BCFD',
      400: '#8098F9',
      main: '#6172F3',
      600: '#444CE7',
      700: '#3538CD',
      800: '#2D31A6',
      900: '#2D3282',
      950: '#1F235B',
      contrastText: '#000000',
      light: '#000000',
      dark: '#000000',
    },
    rose: {
      25: '#FFF5F6',
      50: '#FFF1F3',
      100: '#FFE4E8',
      200: '#FECDD6',
      300: '#FEA3B4',
      400: '#FD6F8E',
      main: '#F63D68',
      600: '#E31B54',
      700: '#C01048',
      800: '#A11043',
      900: '#89123E',
      950: '#510B24',
      contrastText: '#000000',
      light: '#000000',
      dark: '#000000',
    },
  },
  typography: {
    fontFamily: 'Inter, Helvetica, Arial, sans-serif',
    display2Xl: {
      fontSize: '4.5rem',
      lineHeight: '90px',
      fontWeight: 400,
    },
    displayXl: {
      fontSize: '3.75rem',
      lineHeight: '72px',
      fontWeight: 400,
    },
    displayLg: {
      fontSize: '3rem',
      lineHeight: '60px',
      fontWeight: 400,
    },
    displayMd: {
      fontSize: '2.25rem',
      lineHeight: '44px',
      fontWeight: 400,
    },
    displaySm: {
      fontSize: '1.875rem',
      lineHeight: '38px',
      fontWeight: 400,
    },
    displayXs: {
      fontSize: '1.5rem',
      lineHeight: '32px',
      fontWeight: 400,
    },
    textXl: {
      fontSize: '1.25rem',
      lineHeight: '30px',
      fontWeight: 400,
    },
    textLg: {
      fontSize: '1.125rem',
      lineHeight: '28px',
      fontWeight: 400,
    },
    textMd: {
      fontSize: '1rem',
      lineHeight: '24px',
      fontWeight: 400,
    },
    textSm: {
      fontSize: '0.875rem',
      lineHeight: '20px',
      fontWeight: 400,
    },
    textXs: {
      fontSize: '0.75rem',
      lineHeight: '18px',
      fontWeight: 400,
    },
    h1: undefined,
    h2: undefined,
    h3: undefined,
    h4: undefined,
    h5: undefined,
    h6: undefined,
    subtitle1: undefined,
    subtitle2: undefined,
    body1: undefined,
    body2: undefined,
    button: undefined,
    caption: undefined,
  },
  components: {
    MuiDivider: {
      styleOverrides: {
        root: ({ theme: { palette } }) => ({
          borderColor: palette.primary[100],
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          '&.MuiDialog-paperFullScreen': {
            borderRadius: 0,
          },
          borderRadius: 12,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: 24,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: 24,
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: ({ theme: { typography } }) => ({
          fontFamily: 'Inter, Helvetica, Arial, sans-serif',
          ...typography.textSm,
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 16,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: 'Inter, Helvetica, Arial, sans-serif',
        },
        html: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          boxSizing: 'border-box',
          WebkitTextSizeAdjust: '100%',
          scrollbarGutter: 'stable',
          scrollbarWidth: 'none',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: ({ theme: { typography, spacing } }) => ({
          ...typography.textMd,
          fontWeight: 500,
          padding: spacing(6),
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme: { spacing } }) => ({
          padding: spacing(6),
        }),
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: ({ theme: { spacing } }) => ({
          padding: spacing(4),
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme: { palette } }) => ({
          backgroundColor: palette.primary[50],
        }),
      },
    },
    MuiTypography: {
      defaultProps: {
        fontFamily: 'Inter, Helvetica, Arial, sans-serif',
        variantMapping: {
          display2Xl: 'h1',
          displayXl: 'h2',
          displayLg: 'h3',
          displayMd: 'h4',
          displaySm: 'h5',
          displayXs: 'h6',
          textXl: 'p',
          textLg: 'p',
          textMd: 'p',
          textSm: 'p',
          textXs: 'p',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme: { palette, typography } }) => ({
          '&.MuiFormLabel-root': {
            '&.Mui-error': {
              color: palette.secondary[900],
            },
            '& .MuiFormLabel-asterisk': {
              display: 'contents',
              color: palette.error.main,
            },
            display: 'contents',
            color: palette.grey[900],
            whiteSpace: 'unset',
          },
          fontFamily: typography.fontFamily,
          fontSize: typography.textSm.fontSize,
          fontWeight: 500,
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme: { palette, spacing, typography } }) => ({
          '&.MuiFormHelperText-root': {
            margin: spacing(1.5, 0, 0, 0),
            color: palette.secondary[600],
          },
          fontFamily: typography.fontFamily,
          fontSize: typography.textSm.fontSize,
          '&.Mui-error': {
            color: palette.error.main,
          },
        }),
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: ({ theme: { palette, spacing, typography } }) => ({
          marginTop: spacing(1),
          border: '1px solid',
          borderRadius: spacing(2),
          borderColor: palette.grey[200],
          boxShadow: shadowSizes.lg,
          '& .MuiMenuItem-root': {
            fontFamily: typography.fontFamily,
            fontSize: typography.textSm.fontSize,
            borderRadius: spacing(1.5),
            '&.Mui-selected': {
              backgroundColor: palette.primary[25],
            },
            '&:hover': {
              backgroundColor: palette.primary[25],
            },
          },
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme: { palette, spacing, typography } }) => ({
          marginTop: spacing(1),
          border: '1px solid',
          borderRadius: spacing(2),
          borderColor: palette.grey[200],
          boxShadow: shadowSizes.lg,
          '& .MuiMenuItem-root': {
            fontFamily: typography.fontFamily,
            fontSize: typography.textSm.fontSize,
            borderRadius: spacing(1.5),
            '&.Mui-selected': {
              backgroundColor: palette.primary[25],
            },
            '&:hover': {
              backgroundColor: palette.primary[25],
            },
          },
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme: { typography } }) => ({
          ...typography.textSm,
        }),
      },
    },
    MuiButtonBase: {
      defaultProps: {
        // Disable ripple for jest tests
        disableRipple: !!process.env.JEST_WORKER_ID,
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: ({ theme: { typography } }) => ({
          ...typography.textSm,
          fontWeight: 500,
        }),
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme: { spacing } }) => ({
          borderRadius: spacing(2),
        }),
      },
    },
  },
});

export default theme;
